import type { FilterError, FilterNode, ParseFilterResult } from "./ast";
import { parseTerm, TermError } from "./terms";
import { type Token, TokenizeError, tokenize } from "./tokenizer";

export const MAX_QUERY_LENGTH = 1024;

export interface ParseOptions {
  /** Preferred language of dates (`en`, `zh`, or an IETF tag); the other language is tried too. */
  lang?: string | null;
}

class SyntaxErr extends Error {
  constructor(readonly error: FilterError) {
    super(error.message);
  }
}

const DESCRIBE: Record<Token["type"], string> = {
  and: '"&"',
  or: '"|"',
  not: '"!"',
  lparen: '"("',
  rparen: '")"',
  comma: '","',
  text: "text",
};

/**
 * Parses a Todoist filter query into one AST per comma-separated group.
 *
 * Grammar (`!` binds tightest, then `&`, then `|`; `,` separates independent groups):
 *   query  := group ("," group)*
 *   group  := or
 *   or     := and ("|" and)*
 *   and    := unary ("&" unary)*
 *   unary  := "!" unary | "(" or ")" | term
 */
export function parseFilter(query: string, options: ParseOptions = {}): ParseFilterResult {
  try {
    return { ok: true, query: { groups: parseGroups(query, normalizeLang(options.lang)) } };
  } catch (e) {
    if (e instanceof SyntaxErr || e instanceof TermError || e instanceof TokenizeError) {
      return { ok: false, error: e.error };
    }
    throw e;
  }
}

function normalizeLang(lang: string | null | undefined): string | undefined {
  const base = lang?.toLowerCase().split(/[-_]/)[0];
  return base === "zh" || base === "en" ? base : undefined;
}

function parseGroups(query: string, lang: string | undefined): FilterNode[] {
  if (query.length > MAX_QUERY_LENGTH) {
    throw new SyntaxErr({
      code: "QUERY_TOO_LONG",
      message: `Query is longer than ${MAX_QUERY_LENGTH} characters`,
      start: MAX_QUERY_LENGTH,
      end: query.length,
    });
  }
  const tokens = tokenize(query);
  if (tokens.length === 0) {
    throw new SyntaxErr({ code: "EMPTY_QUERY", message: "Empty filter query", start: 0, end: query.length });
  }
  let pos = 0;
  const peek = () => tokens[pos];
  const fail = (code: FilterError["code"], message: string, start: number, end: number): never => {
    throw new SyntaxErr({ code, message, start, end });
  };
  const unexpected = (tok: Token | undefined, expected: string): never =>
    tok
      ? fail(
          "UNEXPECTED_TOKEN",
          `Unexpected ${DESCRIBE[tok.type]} at position ${tok.start}, expected ${expected}`,
          tok.start,
          tok.end,
        )
      : fail("UNEXPECTED_END", `Unexpected end of query, expected ${expected}`, query.length, query.length);

  const span = (a: FilterNode, b: FilterNode) => ({ start: a.span.start, end: b.span.end });

  function parseOr(): FilterNode {
    let left = parseAnd();
    while (peek()?.type === "or") {
      pos++;
      const right = parseAnd();
      left = { type: "or", left, right, span: span(left, right) };
    }
    return left;
  }

  function parseAnd(): FilterNode {
    let left = parseUnary();
    while (peek()?.type === "and") {
      pos++;
      const right = parseUnary();
      left = { type: "and", left, right, span: span(left, right) };
    }
    return left;
  }

  function parseUnary(): FilterNode {
    const tok = peek();
    if (!tok) return unexpected(tok, "a filter");
    if (tok.type === "not") {
      pos++;
      const operand = parseUnary();
      return { type: "not", operand, span: { start: tok.start, end: operand.span.end } };
    }
    if (tok.type === "lparen") {
      pos++;
      const inner = parseOr();
      const close = peek();
      if (close?.type !== "rparen") {
        if (!close || close.type === "comma") {
          fail("UNBALANCED_PARENTHESIS", `Unclosed "(" at position ${tok.start}`, tok.start, tok.end);
        }
        unexpected(close, '"&", "|" or ")"');
      }
      pos++;
      return { ...inner, span: { start: tok.start, end: (close as Token).end } };
    }
    if (tok.type === "text") {
      pos++;
      return { type: "term", term: parseTerm(tok, lang), span: { start: tok.start, end: tok.end }, source: tok.source };
    }
    if (tok.type === "rparen") {
      return fail("UNBALANCED_PARENTHESIS", `Unmatched ")" at position ${tok.start}`, tok.start, tok.end);
    }
    return unexpected(tok, "a filter");
  }

  const groups: FilterNode[] = [];
  for (;;) {
    const tok = peek();
    if (!tok || tok.type === "comma") {
      const at = tok?.start ?? query.length;
      fail("EMPTY_GROUP", `Empty filter before position ${at}`, at, tok ? tok.end : at);
    }
    groups.push(parseOr());
    const next = peek();
    if (!next) break;
    if (next.type === "comma") {
      pos++;
      continue;
    }
    if (next.type === "rparen") {
      fail("UNBALANCED_PARENTHESIS", `Unmatched ")" at position ${next.start}`, next.start, next.end);
    }
    unexpected(next, '"&", "|" or ","');
  }
  return groups;
}
